import { useState, useEffect } from 'react';
import { Grid } from '@mui/material';
import Table from '../../components/Table';
import SuccessError from 'components/SuccessError';
import {
  tableOptionFilter,
  columOptions,
  isUser,
  handleDownload,
  isAreaAdmin,
  isAdmin,
  formatTierName
} from 'utils/utils';
import { authPostRequest } from 'Api/api';
import CreateAndUpdateMarkupPrice from './CreateAndUpdateMarkupPrice';
import { style } from 'constant/constants';
import Text from 'components/Text';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Loader from 'components/Loader';
import MarkupMenu from 'components/MarkupMenu';
import TextEllipsize from 'utils/TextEllipsize';
import PricesColumn from 'components/PricesColumn';
import TiersHeadLabel from 'components/TiersHeadLabel';
import { TIERS } from 'constant/enums';

const isSort = {
  ...columOptions,
  sort: true
  // direction: 'asc'
};

const columns = [
  {
    name: 'bracketName',
    label: 'Name',
    options: { ...isSort, customBodyRender: (value) => <TextEllipsize value={value} length={20} /> }
  },

  {
    name: 'bracketType',
    label: 'Bracket Type',
    options: isSort
  },

  {
    name: 'material',
    label: 'Material Type',
    options: { ...isSort, customBodyRender: (value) => <TextEllipsize value={value} length={20} /> }
  }
];

//options and override onDownload method
const options = {
  ...tableOptionFilter,
  download: true,
  downloadOptions: {
    filename: 'PriceList',
    filterOptions: {
      useDisplayedColumnsOnly: true,
      useDisplayedRowsOnly: true
    }
  }
};

const Prices = () => {
  const [addOpenModal, setAddOpenModal] = useState(false);
  const [showAlertBar, setShowAlertBar] = useState(false);
  const [isFormSaved, setIsFormSaved] = useState(false);
  const [successMessage, setSuccessMessage] = useState([]);
  const [showLoader, setShowLoader] = useState(false);
  const { userData, storeData, adminAccess } = useSelector((store) => store.auth);
  const [tierName, setTierName] = useState();
  const [tierPercentage, setTierPercentage] = useState();
  const [currentTierName, setCurrentTierName] = useState();
  const [brackets, setBrackets] = useState();
  const [tiers, setTiers] = useState([]);

  const navigate = useNavigate();

  const getBracketPriceList = async () => {
    const storeID = {
      storeId: adminAccess
        ? storeData?.id
        : isUser(userData?.roles) || isAdmin(userData?.roles)
          ? userData?.storesList[0]?.storeId
          : null
    };

    if (
      !storeID?.storeId ||
      storeID?.storeId == null ||
      storeID?.storeId == undefined ||
      isAreaAdmin(userData?.roles)
    ) {
      navigate('/Stores');
    }
    try {
      setShowLoader(true);
      const list = await authPostRequest('getStorePriceList', storeID);
      setTierPercentage(list?.currentTierPercentage);
      setBrackets(list?.storePricesList);
      setTierName(list?.currentTier);
      setCurrentTierName(list?.currentTier);

      setTiers([
        {
          name: 'Tier 2',
          percentage: list?.tier2Percentage
        },
        {
          name: 'Tier 3',
          percentage: list?.tier3Percentage
        },
        {
          name: 'Tier 4',
          percentage: list?.tier4Percentage
        },
        {
          name: 'Tier 5',
          percentage: list?.tier5Percentage
        }
      ]);

      setShowLoader(false);
    } catch (err) {
      setShowLoader(false);
      console.log(err);
    }
  };

  useEffect(() => {
    getBracketPriceList();
  }, []);

  useEffect(() => {
    getBracketPriceList();
  }, [isFormSaved]);

  const newOptions = {
    ...options,

    onDownload: (buildHead, buildBody, columns, data) => {
      handleDownload(buildHead, buildBody, columns, data);
      return '\uFEFF' + buildHead(columns) + buildBody(data);

      //for cancel csv download
      // return false;
      //for download csv
    },
    onTableChange: (action, tableState) => {
      if (action === 'filterChange') {
        // Extract the filtered data
        const filteredRows = tableState.displayData.map((row) => brackets[row.dataIndex]);

        // Update download option based on filtered data
        const downloadEnabled = filteredRows.length > 0;
        options.download = downloadEnabled;
      }
    }
  };

  return (
    <>
      <Grid display="flex" alignItems="center" justifyContent="space-between">
        <Text variant="h4" style={{ color: style.LIGHT.primary, marginBottom: '1vh' }}>
          Prices
        </Text>
        {isUser(userData.roles) || (
          <Grid display="flex" sx={{ marginBottom: '1vh' }}>
            <MarkupMenu
              setAddOpenModal={setAddOpenModal}
              setTierName={setTierName}
              setTierPercentage={setTierPercentage}
              tiers={tiers}
            />
          </Grid>
        )}
      </Grid>
      {showAlertBar && (
        <SuccessError successMessage={successMessage} setShowAlertBar={setShowAlertBar} />
      )}
      <Table
        data={brackets}
        options={newOptions}
        columns={[
          ...columns,
          {
            name: 'wholesalePrice',
            label: 'Tier 1 ($)',
            options: {
              display: false,
              ...isSort,
              customBodyRender: (value) => <PricesColumn value={value} />
            }
          },

          {
            name: 'tier2Price',
            label: 'Tier 2 ($)',
            options: {
              ...isSort,
              sort: formatTierName(TIERS.TIER2) === currentTierName,
              display: formatTierName(TIERS.TIER2) === currentTierName,
              customBodyRender: (value) => <PricesColumn value={value} />,
              customHeadLabelRender: (columnMeta) => (
                <TiersHeadLabel
                  columnMeta={columnMeta}
                  tier={TIERS.TIER2}
                  currentTierName={currentTierName}
                />
              )
            }
          },

          {
            name: 'tier3Price',
            label: 'Tier 3 ($)',
            options: {
              ...isSort,
              sort: formatTierName(TIERS.TIER3) === currentTierName,
              display: formatTierName(TIERS.TIER3) === currentTierName,
              customBodyRender: (value) => <PricesColumn value={value} />,
              customHeadLabelRender: (columnMeta) => (
                <TiersHeadLabel
                  columnMeta={columnMeta}
                  tier={TIERS.TIER3}
                  currentTierName={currentTierName}
                />
              )
            }
          },

          {
            name: 'tier4Price',
            label: 'Tier 4 ($)',
            options: {
              ...isSort,
              sort: formatTierName(TIERS.TIER4) === currentTierName,
              display: formatTierName(TIERS.TIER4) === currentTierName,
              customBodyRender: (value) => <PricesColumn value={value} />,
              customHeadLabelRender: (columnMeta) => (
                <TiersHeadLabel
                  columnMeta={columnMeta}
                  tier={TIERS.TIER4}
                  currentTierName={currentTierName}
                />
              )
            }
          },

          {
            name: 'tier5Price',
            label: 'Tier 5 ($)',

            options: {
              ...isSort,
              sort: formatTierName(TIERS.TIER5) === currentTierName,
              display: formatTierName(TIERS.TIER5) === currentTierName,
              customBodyRender: (value) => <PricesColumn value={value} />,
              customHeadLabelRender: (columnMeta) => (
                <TiersHeadLabel
                  columnMeta={columnMeta}
                  tier={TIERS.TIER5}
                  currentTierName={currentTierName}
                />
              )
            }
          }
        ]}
      />

      {showLoader && <Loader open={showLoader} />}
      {addOpenModal && (
        <CreateAndUpdateMarkupPrice
          currentTier={currentTierName}
          tierName={tierName}
          markup={tierPercentage}
          addOpenModal={addOpenModal}
          getBracketPriceList={getBracketPriceList}
          setAddOpenModal={setAddOpenModal}
          setSuccess={setSuccessMessage}
          setAlertBar={setShowAlertBar}
          setIsFormSaved={setIsFormSaved}
          isFormSaved={isFormSaved}
        />
      )}
    </>
  );
};

export default Prices;
